/**
 * ICS (iCalendar) Export Utility
 *
 * Generates a valid `.ics` file from a ScheduledEvent and triggers a
 * browser download. The resulting file imports cleanly into Google
 * Calendar, Apple Calendar, Outlook, and any other RFC 5545-compliant
 * client.
 *
 * Why client-side rather than a server endpoint?
 *   - The file is tiny and fully derivable from the event data already
 *     in the page; no round-trip needed.
 *   - Avoids backend complexity around timezones (the server data we
 *     show was already converted to a string the user expects).
 *   - Keeps "Add to Calendar" instant from the user's perspective —
 *     no spinner, no network failure mode.
 *
 * What we deliberately do NOT do:
 *   - Recurrence rules (RRULE). Frequency is on the WO record but
 *     surfacing it in calendar exports is a separate decision.
 *   - VTIMEZONE blocks. We export UTC times for timed events and
 *     date-only values for all-day events; both interpretations are
 *     unambiguous to compliant clients.
 *
 * Usage:
 *   import { downloadEventAsIcs } from '../../utils/icsExport';
 *   downloadEventAsIcs(event);
 */

import type { ScheduledEvent } from '../services/scheduledEventsService';

/**
 * Format a Date in the iCalendar UTC datetime format (YYYYMMDDTHHmmssZ).
 * No separators — that's the spec.
 */
function formatIcsDateTime(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
        `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
        'T' +
        `${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
    );
}

/**
 * Format a Date in the iCalendar date-only form (YYYYMMDD).
 * Used for VALUE=DATE properties on all-day events. Local interpretation
 * — no UTC conversion (RFC 5545 §3.3.4).
 */
function formatIcsDate(value: string | Date): string {
    // Accept either a YYYY-MM-DD string (treat as local) or a Date.
    const d = typeof value === 'string'
        ? new Date(`${value}T00:00:00`)
        : value;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

/**
 * Escape a string for use as an iCalendar property value.
 * Per RFC 5545: backslashes, semicolons, commas, and newlines must be
 * backslash-escaped; CRLFs become \\n.
 */
function escapeIcsText(value: string): string {
    return value
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r\n|\r|\n/g, '\\n');
}

/**
 * Fold a long iCalendar content line per RFC 5545 §3.1: lines longer
 * than 75 octets must be broken with CRLF + a single leading whitespace.
 * Compliant clients reassemble the value before parsing.
 */
function foldLine(line: string): string {
    if (line.length <= 75) return line;
    const chunks: string[] = [];
    let i = 0;
    while (i < line.length) {
        chunks.push((i === 0 ? '' : ' ') + line.slice(i, i + (i === 0 ? 75 : 74)));
        i += i === 0 ? 75 : 74;
    }
    return chunks.join('\r\n');
}

/**
 * Build the multi-line LOCATION value from the event's address fields.
 * Returns null when the event has no address at all (omits the property
 * rather than emitting a meaningless empty LOCATION line).
 */
function buildLocationLine(event: ScheduledEvent): string | null {
    const parts = [event.address, event.addressLine2, event.city && event.state ? `${event.city}, ${event.state}` : event.city || event.state, event.zip]
        .filter(Boolean) as string[];
    if (parts.length === 0) return null;
    return parts.join(', ');
}

/**
 * Build the SUMMARY value — short title shown in the calendar grid.
 */
function buildSummary(event: ScheduledEvent): string {
    return event.title || (event.recordType === 'inspection' ? 'Inspection' : 'Work Order');
}

/**
 * Build the DESCRIPTION value — multi-line context the user sees when
 * opening the imported event. Includes the company, assignees, status,
 * and a deep-link back to the record in the GBrands app for one-click
 * navigation from their personal calendar.
 */
function buildDescription(event: ScheduledEvent): string {
    const lines: string[] = [];
    if (event.companyName) lines.push(`Company: ${event.companyName}`);
    if (event.locationName) lines.push(`Location: ${event.locationName}`);
    if (event.status) lines.push(`Status: ${event.status}`);
    if (event.assignees.length > 0) {
        lines.push(`Assigned: ${event.assignees.map((p) => p.name).join(', ')}`);
    }
    if (event.detailUrl) {
        // Use the current origin so the link opens whichever environment
        // the user is currently logged into (dev/qa/prod).
        const base = typeof window !== 'undefined' ? window.location.origin : '';
        lines.push(`Open in app: ${base}${event.detailUrl}`);
    }
    return lines.join('\n');
}

/**
 * Generate the raw iCalendar text for a ScheduledEvent.
 * Exported so callers can compose into a multi-event export later if
 * needed; the public download helper below wraps + triggers the file.
 */
export function buildIcsForEvent(event: ScheduledEvent): string {
    // Stable UID per event so re-imports update the same calendar entry
    // (clients dedupe by UID per RFC 5545). Append the host to keep
    // UIDs unique across organizations/installations.
    const host = typeof window !== 'undefined' ? window.location.hostname : 'gbrands';
    const uid = `${event.id}@${host}`;

    // Timestamp marking when this iCalendar object was produced.
    // Required by the spec (DTSTAMP).
    const dtstamp = formatIcsDateTime(new Date());

    // Build start/end. All-day events use VALUE=DATE; timed events use
    // UTC datetime form.
    let dtStartLine: string;
    let dtEndLine: string;
    if (event.allDay) {
        dtStartLine = `DTSTART;VALUE=DATE:${formatIcsDate(event.scheduledDate)}`;
        // End-exclusive — bump to next day for an all-day single event.
        const next = new Date(`${event.scheduledDate}T00:00:00`);
        next.setDate(next.getDate() + 1);
        dtEndLine = `DTEND;VALUE=DATE:${formatIcsDate(next)}`;
    } else {
        dtStartLine = `DTSTART:${formatIcsDateTime(new Date(event.start))}`;
        dtEndLine = `DTEND:${formatIcsDateTime(new Date(event.end || event.start))}`;
    }

    const properties: string[] = [
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${dtstamp}`,
        dtStartLine,
        dtEndLine,
        `SUMMARY:${escapeIcsText(buildSummary(event))}`,
        `DESCRIPTION:${escapeIcsText(buildDescription(event))}`,
    ];

    const location = buildLocationLine(event);
    if (location) properties.push(`LOCATION:${escapeIcsText(location)}`);

    properties.push('END:VEVENT');

    const calendar: string[] = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        // PRODID identifies the producer; conventional format is
        // -//Vendor//Product//Language. Helps debugging when imported
        // events look weird in some client.
        'PRODID:-//GBrands//Work Order Platform//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        ...properties,
        'END:VCALENDAR',
    ];

    // Per RFC 5545, lines are CRLF-delimited and folded if >75 octets.
    return calendar.map(foldLine).join('\r\n') + '\r\n';
}

/**
 * Build a safe filename from the event title. Strips characters that
 * filesystems/browsers tend to choke on; preserves spaces converted to
 * underscores so the result stays human-readable.
 */
function buildFilename(event: ScheduledEvent): string {
    const cleanedTitle = (event.title || event.recordType)
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '_')
        .slice(0, 64);
    return `${cleanedTitle || 'event'}_${event.id}.ics`;
}

/**
 * Generate an .ics file for the given event and trigger a browser
 * download. Safe to call from a click handler (must be a user
 * gesture — browsers block programmatic downloads otherwise).
 */
export function downloadEventAsIcs(event: ScheduledEvent): void {
    const ics = buildIcsForEvent(event);
    // Standard MIME type for iCalendar files per RFC 5545.
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    // Programmatic anchor click is the most reliable cross-browser way
    // to trigger a file download with a chosen filename.
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = buildFilename(event);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // Release the blob URL to allow GC of the underlying memory.
    URL.revokeObjectURL(url);
}
